import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Users, Search, Shield, ShieldAlert, ShieldCheck, Crown, Ban, UserCheck,
  ChevronLeft, ChevronRight, MoreHorizontal, Eye, X, Check,
  Clock, AlertTriangle, FileText, Gift, Star, Zap, Activity, StickyNote,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const ROLE_COLORS = {
  super_admin: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  admin: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  senior_moderator: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  moderator: "bg-green-500/10 text-green-400 border-green-500/30",
  content_editor: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  channel_manager: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  support_manager: "bg-pink-500/10 text-pink-400 border-pink-500/30",
  regular_user: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  suspended_user: "bg-red-500/10 text-red-400 border-red-500/30",
};

const ROLE_LABELS = {
  super_admin: "Super Admin", admin: "Admin", senior_moderator: "Senior Moderator",
  moderator: "Moderator", content_editor: "Content Editor", channel_manager: "Channel Manager",
  support_manager: "Support Manager", regular_user: "User", suspended_user: "Suspended",
};

function RoleBadge({ role }) {
  return (
    <span className={cn("px-2.5 py-0.5 rounded-full text-[11px] font-semibold border", ROLE_COLORS[role] || ROLE_COLORS.regular_user)}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

export default function AdminUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(searchParams.get("filter") || "");
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [presets, setPresets] = useState({});

  // Modals
  const [roleModal, setRoleModal] = useState(null);
  const [modModal, setModModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [permModal, setPermModal] = useState(null);
  const [membershipFilter, setMembershipFilter] = useState("");
  const [grantModal, setGrantModal] = useState(null);
  const [manageModal, setManageModal] = useState(null);
  const [revokeModal, setRevokeModal] = useState(null);
  const [premiumForm, setPremiumForm] = useState({ duration: "30d", custom_expiration: "", reason: "", internal_note: "", send_notification: true });
  const [premiumBusy, setPremiumBusy] = useState(false);
  const [gstats, setGstats] = useState(null);
  const [expWindow, setExpWindow] = useState(7);
  const [expiring, setExpiring] = useState(null);

  // Form state
  const [selectedRole, setSelectedRole] = useState("");
  const [roleReason, setRoleReason] = useState("");
  const [roleExpiry, setRoleExpiry] = useState("");
  const [modPreset, setModPreset] = useState("basic_moderator");
  const [modReason, setModReason] = useState("");
  const [modExpiry, setModExpiry] = useState("");
  const [customPerms, setCustomPerms] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/enhanced`, {
        params: { page, search, role_filter: roleFilter, membership_filter: membershipFilter }, withCredentials: true,
      });
      setUsers(res.data.users); setTotal(res.data.total); setPages(res.data.pages);
    } catch { }
    setLoading(false);
  }, [page, search, roleFilter, membershipFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchGlobalStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users/stats`, { withCredentials: true });
      setGstats(r.data);
    } catch { }
  }, []);

  const fetchExpiring = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users/premium/expiring`, { params: { days: expWindow }, withCredentials: true });
      setExpiring(r.data);
    } catch { }
  }, [expWindow]);

  useEffect(() => { fetchGlobalStats(); }, [fetchGlobalStats]);
  useEffect(() => { fetchExpiring(); }, [fetchExpiring]);

  useEffect(() => {
    axios.get(`${API}/admin/rbac/roles`, { withCredentials: true }).then(r => setRoles(r.data)).catch(() => {});
    axios.get(`${API}/admin/rbac/presets`, { withCredentials: true }).then(r => setPresets(r.data)).catch(() => {});
    axios.get(`${API}/admin/rbac/permissions`, { withCredentials: true }).then(r => setAllPermissions(r.data)).catch(() => {});
  }, []);

  const handleAssignRole = async () => {
    if (!roleModal || !selectedRole) return;
    try {
      await axios.put(`${API}/admin/users/${roleModal.id}/role`, {
        role_id: selectedRole, reason: roleReason, expires_at: roleExpiry || null,
      }, { withCredentials: true });
      setRoleModal(null); fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const handleAssignModerator = async () => {
    if (!modModal) return;
    try {
      const perms = modPreset === "custom" ? customPerms
        : presets[modPreset]?.permissions || [];
      await axios.post(`${API}/admin/users/${modModal.id}/moderator`, {
        permission_preset: modPreset, custom_permissions: perms,
        reason: modReason, expires_at: modExpiry || null,
      }, { withCredentials: true });
      setModModal(null); fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const handleRevokeModerator = async (user) => {
    if (!confirm(`Revoke moderator access from ${user.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}/moderator`, { withCredentials: true, params: { reason: "Admin decision" } });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const handleSuspend = async (user) => {
    const reason = prompt(`Reason for suspending ${user.email}:`);
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/users/${user.id}/suspend`, { reason }, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const handleReactivate = async (user) => {
    if (!confirm(`Reactivate ${user.email}?`)) return;
    try {
      await axios.post(`${API}/admin/users/${user.id}/reactivate`, {}, { withCredentials: true });
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const openModPermissions = (user) => {
    setCustomPerms(user.moderator_permissions || []);
    setModPreset(user.moderator_preset || "custom");
    setPermModal(user);
  };

  const handleSavePermissions = async () => {
    if (!permModal) return;
    try {
      const perms = modPreset === "custom" ? customPerms
        : presets[modPreset]?.permissions || [];
      await axios.put(`${API}/admin/users/${permModal.id}/permissions`, {
        permissions: perms, preset: modPreset,
      }, { withCredentials: true });
      setPermModal(null); fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const openUserDetail = async (user) => {
    try {
      const res = await axios.get(`${API}/admin/users/${user.id}/detail`, { withCredentials: true });
      setDetailModal(res.data);
    } catch { }
  };

  const openGrant = (user) => {
    setPremiumForm({ duration: "30d", custom_expiration: "", reason: "", internal_note: "", send_notification: true });
    setGrantModal(user);
  };
  const openManage = (user) => {
    setPremiumForm({ duration: user.is_lifetime ? "lifetime" : "custom", custom_expiration: (user.premium_expiration || "").slice(0, 10), reason: "", internal_note: "", send_notification: false });
    setManageModal(user);
  };
  const openRevoke = (user) => {
    setPremiumForm({ duration: "", custom_expiration: "", reason: "", internal_note: "", send_notification: true });
    setRevokeModal(user);
  };

  const submitGrant = async () => {
    setPremiumBusy(true);
    try {
      const body = { duration: premiumForm.duration, reason: premiumForm.reason, internal_note: premiumForm.internal_note, send_notification: premiumForm.send_notification };
      if (premiumForm.duration === "custom") body.custom_expiration = new Date(premiumForm.custom_expiration).toISOString();
      await axios.post(`${API}/admin/users/${grantModal.id}/premium/grant`, body, { withCredentials: true });
      setGrantModal(null); fetchUsers(); fetchGlobalStats(); fetchExpiring();
    } catch (e) { alert(e.response?.data?.detail || "Failed to grant premium"); }
    finally { setPremiumBusy(false); }
  };
  const submitManage = async () => {
    setPremiumBusy(true);
    try {
      const body = { duration: premiumForm.duration, reason: premiumForm.reason, send_notification: premiumForm.send_notification };
      if (premiumForm.duration === "custom") body.custom_expiration = new Date(premiumForm.custom_expiration).toISOString();
      await axios.patch(`${API}/admin/users/${manageModal.id}/premium/manage`, body, { withCredentials: true });
      setManageModal(null); fetchUsers(); fetchGlobalStats(); fetchExpiring();
    } catch (e) { alert(e.response?.data?.detail || "Failed to update premium"); }
    finally { setPremiumBusy(false); }
  };
  const submitRevoke = async () => {
    setPremiumBusy(true);
    try {
      await axios.post(`${API}/admin/users/${revokeModal.id}/premium/revoke`, { reason: premiumForm.reason, send_notification: premiumForm.send_notification }, { withCredentials: true });
      setRevokeModal(null); fetchUsers(); fetchGlobalStats(); fetchExpiring();
    } catch (e) { alert(e.response?.data?.detail || "Failed to revoke premium"); }
    finally { setPremiumBusy(false); }
  };

  const membershipFilters = [
    { label: "All Members", value: "" },
    { label: "Free", value: "free" },
    { label: "Premium", value: "premium" },
    { label: "Paid", value: "paid" },
    { label: "Complimentary", value: "complimentary" },
    { label: "Expired", value: "expired" },
  ];

  const filterButtons = [
    { label: "All", value: "" },
    { label: "Moderators", value: "moderator" },
    { label: "Admins", value: "admin" },
    { label: "Suspended", value: "suspended_user" },
  ];

  return (
    <>
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Users", value: gstats?.total_users ?? total, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
          { label: "Paid Premium", value: gstats?.paid_members ?? 0, icon: Crown, color: "text-green-400", bg: "bg-green-500/10" },
          { label: "Complimentary", value: gstats?.comp_members ?? 0, icon: Gift, color: "text-amber-400", bg: "bg-amber-500/10" },
          { label: "Suspended", value: gstats?.suspended_members ?? 0, icon: Ban, color: "text-red-400", bg: "bg-red-500/10" },
        ].map((s, i) => (
          <div key={i} className="bg-slate-900 rounded-xl border border-slate-800 p-4 flex items-center gap-3" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={cn("p-2.5 rounded-lg", s.bg)}><s.icon className={s.color} size={18} /></div>
            <div><div className="text-xl font-bold text-white">{s.value}</div><div className="text-xs text-slate-500">{s.label}</div></div>
          </div>
        ))}
      </div>

      {/* Expiring Complimentary Premium widget */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 mb-6 overflow-hidden" data-testid="expiring-premium-widget">
        <div className="p-4 border-b border-slate-800 flex items-center gap-3 flex-wrap">
          <div className="p-2 rounded-lg bg-amber-500/10"><Clock className="text-amber-400" size={16} /></div>
          <h3 className="text-sm font-bold text-white">Expiring Complimentary Premium</h3>
          <span className="text-xs text-slate-500">
            {expiring ? `${expiring.count} expiring in the next ${expWindow} days` : "…"}
          </span>
          <div className="ml-auto flex gap-1.5">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setExpWindow(d)}
                className={cn("px-2.5 py-1 rounded-lg text-xs font-medium transition-colors",
                  expWindow === d ? "bg-amber-500 text-black" : "bg-slate-800 text-slate-400 hover:text-white")}
                data-testid={`expiring-window-${d}`}>{d}d</button>
            ))}
          </div>
        </div>
        {expiring && expiring.count > 0 ? (
          <div className="divide-y divide-slate-800/50 max-h-72 overflow-y-auto">
            {expiring.expiring.map(u => (
              <div key={u.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-800/20" data-testid={`expiring-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={cn("px-2 py-0.5 rounded-full text-xs font-semibold",
                    (u.days_left ?? 99) <= 3 ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400")}>
                    {u.days_left === 0 ? "Today" : `${u.days_left}d left`}
                  </span>
                  <span className="text-xs text-slate-500 hidden sm:inline">{(u.expires_at || "").slice(0, 10)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="expiring-empty">
            No complimentary grants expiring in the next {expWindow} days.
          </div>
        )}
      </div>

      {/* Search + Filters */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 mb-6 w-full" data-testid="user-filters">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input type="text" placeholder="Search by name, email..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500"
              data-testid="user-search-input" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {filterButtons.map(f => (
              <button key={f.value} onClick={() => { setRoleFilter(f.value); setPage(1); }}
                className={cn("px-3 py-2 rounded-lg text-xs font-medium transition-colors",
                  roleFilter === f.value ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white")}
                data-testid={`filter-${f.label.toLowerCase()}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap mt-3 pt-3 border-t border-slate-800" data-testid="membership-filters">
          <span className="text-xs text-slate-500 self-center mr-1">Membership:</span>
          {membershipFilters.map(f => (
            <button key={f.value} onClick={() => { setMembershipFilter(f.value); setPage(1); }}
              className={cn("px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                membershipFilter === f.value ? "bg-amber-500 text-black" : "bg-slate-800 text-slate-400 hover:text-white")}
              data-testid={`mfilter-${f.value || "all"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden w-full" data-testid="users-table">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                {["User", "Role", "Status", "Membership", "Source", "Expiration", "Actions"].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors" data-testid={`user-row-${user.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                        {(user.name || user.email)?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{user.name || "—"}</div>
                        <div className="text-xs text-slate-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <RoleBadge role={user.is_suspended ? "suspended_user" : user.role} />
                    {user.is_moderator && <span className="ml-1.5 text-[10px] text-green-400">MOD</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn("text-xs font-medium", user.is_suspended ? "text-red-400" : "text-green-400")}>
                      {user.is_suspended ? "Suspended" : "Active"}
                    </span>
                    {user.role_expires_at && (
                      <div className="text-[10px] text-amber-400 flex items-center gap-1 mt-0.5">
                        <Clock size={10} /> Expires {new Date(user.role_expires_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <MembershipBadge user={user} />
                  </td>
                  <td className="px-4 py-3">
                    <SourceBadge source={user.membership_source} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-slate-400">
                      {user.membership_type !== "premium" ? "—"
                        : user.is_lifetime ? "Lifetime"
                        : user.premium_expiration ? new Date(user.premium_expiration).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                        : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <UserActions user={user}
                      onView={() => openUserDetail(user)}
                      onEditRole={() => { setSelectedRole(user.role); setRoleReason(""); setRoleExpiry(""); setRoleModal(user); }}
                      onMakeMod={() => { setModPreset("basic_moderator"); setModReason(""); setModExpiry(""); setModModal(user); }}
                      onRevokeMod={() => handleRevokeModerator(user)}
                      onModPerms={() => openModPermissions(user)}
                      onSuspend={() => handleSuspend(user)}
                      onReactivate={() => handleReactivate(user)}
                      onGrantPremium={() => openGrant(user)}
                      onManagePremium={() => openManage(user)}
                      onRevokePremium={() => openRevoke(user)}
                    />
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-slate-800/50">
          {users.map(user => (
            <div key={user.id} className="p-4" data-testid={`user-card-${user.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
                    {(user.name || user.email)?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{user.name || "—"}</div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                  </div>
                </div>
                <RoleBadge role={user.is_suspended ? "suspended_user" : user.role} />
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => openUserDetail(user)} className="px-2 py-1 rounded bg-slate-800 text-slate-400 text-xs hover:text-white">View</button>
                <button onClick={() => { setSelectedRole(user.role); setRoleModal(user); }} className="px-2 py-1 rounded bg-slate-800 text-blue-400 text-xs hover:text-blue-300">Edit Role</button>
                {!user.is_moderator && !user.is_suspended && (
                  <button onClick={() => { setModPreset("basic_moderator"); setModModal(user); }} className="px-2 py-1 rounded bg-green-500/10 text-green-400 text-xs">Make Mod</button>
                )}
                {user.is_moderator && (
                  <button onClick={() => handleRevokeModerator(user)} className="px-2 py-1 rounded bg-red-500/10 text-red-400 text-xs">Revoke Mod</button>
                )}
                {!user.is_comp_member && (
                  <button onClick={() => openGrant(user)} className="px-2 py-1 rounded bg-amber-500/10 text-amber-400 text-xs" data-testid={`card-grant-${user.id}`}>Grant Premium</button>
                )}
                {user.is_comp_member && (
                  <>
                    <button onClick={() => openManage(user)} className="px-2 py-1 rounded bg-amber-500/10 text-amber-400 text-xs">Manage Premium</button>
                    <button onClick={() => openRevoke(user)} className="px-2 py-1 rounded bg-red-500/10 text-red-400 text-xs">Revoke</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
            <span className="text-xs text-slate-500">Page {page} of {pages} ({total} users)</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded bg-slate-800 text-slate-400 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                className="p-1.5 rounded bg-slate-800 text-slate-400 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>

      {/* ===== ROLE ASSIGNMENT MODAL ===== */}
      {roleModal && (
        <Modal title="Edit Role" onClose={() => setRoleModal(null)}>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">User: <span className="text-white font-medium">{roleModal.email}</span></p>
              <p className="text-sm text-slate-400">Current: <RoleBadge role={roleModal.role} /></p>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">New Role</label>
              <select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                data-testid="role-select">
                {roles.filter(r => r.id !== 'super_admin' && r.id !== 'suspended_user').map(r => (
                  <option key={r.id} value={r.id}>{r.name} (Level {r.level})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Reason</label>
              <input type="text" value={roleReason} onChange={e => setRoleReason(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                placeholder="Optional reason..." data-testid="role-reason-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Expiration Date (optional)</label>
              <input type="datetime-local" value={roleExpiry} onChange={e => setRoleExpiry(e.target.value ? new Date(e.target.value).toISOString() : "")}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRoleModal(null)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm hover:text-white">Cancel</button>
              <button onClick={handleAssignRole} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="assign-role-btn">
                Assign Role
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== MODERATOR ASSIGNMENT MODAL ===== */}
      {modModal && (
        <Modal title="Assign Moderator" onClose={() => setModModal(null)}>
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm text-amber-300">
              You are assigning moderation privileges to this user. They will gain access only to the permissions selected below.
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">User:</span> <span className="text-white">{modModal.email}</span></div>
              <div><span className="text-slate-500">Current Role:</span> <RoleBadge role={modModal.role} /></div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Permission Preset</label>
              <select value={modPreset} onChange={e => setModPreset(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                data-testid="mod-preset-select">
                {Object.entries(presets).map(([k, v]) => (
                  <option key={k} value={k}>{v.name}</option>
                ))}
                <option value="custom">Custom</option>
              </select>
            </div>
            {modPreset !== "custom" && presets[modPreset] && (
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Permissions included:</label>
                <div className="flex flex-wrap gap-1.5">
                  {presets[modPreset].permissions.map(p => (
                    <span key={p} className="px-2 py-0.5 rounded bg-slate-800 text-[11px] text-slate-300">{p.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            {modPreset === "custom" && (
              <PermissionCheckboxes allPermissions={allPermissions} selected={customPerms} onChange={setCustomPerms} />
            )}
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Reason</label>
              <input type="text" value={modReason} onChange={e => setModReason(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none"
                placeholder="Assignment reason..." data-testid="mod-reason-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Expiration Date (optional)</label>
              <input type="datetime-local" value={modExpiry} onChange={e => setModExpiry(e.target.value ? new Date(e.target.value).toISOString() : "")}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModModal(null)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm hover:text-white">Cancel</button>
              <button onClick={handleAssignModerator} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-500" data-testid="assign-mod-btn">
                <Shield size={14} className="inline mr-1" /> Assign Moderator
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== PERMISSION EDITOR MODAL ===== */}
      {permModal && (
        <Modal title="Edit Moderator Permissions" onClose={() => setPermModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Editing permissions for <span className="text-white font-medium">{permModal.email}</span></p>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Preset</label>
              <select value={modPreset} onChange={e => {
                setModPreset(e.target.value);
                if (e.target.value !== "custom" && presets[e.target.value]) {
                  setCustomPerms(presets[e.target.value].permissions);
                }
              }}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-blue-500 focus:outline-none">
                {Object.entries(presets).map(([k, v]) => (<option key={k} value={k}>{v.name}</option>))}
                <option value="custom">Custom</option>
              </select>
            </div>
            <PermissionCheckboxes allPermissions={allPermissions} selected={customPerms} onChange={(perms) => { setCustomPerms(perms); setModPreset("custom"); }} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setPermModal(null)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm hover:text-white">Cancel</button>
              <button onClick={handleSavePermissions} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="save-perms-btn">
                Save Permissions
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== USER DETAIL MODAL ===== */}
      {detailModal && (
        <Modal title="User Detail" onClose={() => setDetailModal(null)} wide>
          <div className="space-y-5 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">Name:</span> <span className="text-white">{detailModal.name || "—"}</span></div>
              <div><span className="text-slate-500">Email:</span> <span className="text-white">{detailModal.email}</span></div>
              <div><span className="text-slate-500">Role:</span> <RoleBadge role={detailModal.current_role?.role_id || "regular_user"} /></div>
              <div><span className="text-slate-500">Created:</span> <span className="text-white">{new Date(detailModal.created_at).toLocaleDateString()}</span></div>
            </div>

            {detailModal.moderator_assignment && (
              <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-green-400 mb-2">Active Moderator Assignment</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Preset:</span> <span className="text-white">{detailModal.moderator_assignment.permission_preset}</span></div>
                  <div><span className="text-slate-500">Assigned by:</span> <span className="text-white">{detailModal.moderator_assignment.assigned_by_name}</span></div>
                  <div><span className="text-slate-500">Permissions:</span> <span className="text-white">{detailModal.moderator_assignment.custom_permissions?.length || 0}</span></div>
                  <div><span className="text-slate-500">Expires:</span> <span className="text-white">{detailModal.moderator_assignment.expires_at ? new Date(detailModal.moderator_assignment.expires_at).toLocaleDateString() : "Never"}</span></div>
                </div>
              </div>
            )}

            {detailModal.role_history?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-400 mb-2">Role History</h4>
                <div className="space-y-2">
                  {detailModal.role_history.map((rh, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-2.5 text-xs">
                      <RoleBadge role={rh.role_id} />
                      <span className="text-slate-500">{rh.is_active ? "Active" : "Revoked"}</span>
                      <span className="text-slate-500">{new Date(rh.assigned_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detailModal.activity?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-400 mb-2">Recent Activity</h4>
                <div className="space-y-1.5">
                  {detailModal.activity.slice(0, 10).map((a, i) => (
                    <div key={i} className="text-xs text-slate-400 flex justify-between">
                      <span>{a.action.replace(/_/g, " ")}: {a.target_name || "—"}</span>
                      <span className="text-slate-600">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Grant Premium Modal */}
      {grantModal && (
        <Modal title="Grant Complimentary Premium" onClose={() => setGrantModal(null)}>
          <div className="space-y-4" data-testid="grant-premium-modal">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">User</span>
              <span className="text-white font-medium">{grantModal.email}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Current Membership</span>
              <MembershipBadge user={grantModal} />
            </div>
            {grantModal.has_paid_subscription && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-300 flex items-start gap-2" data-testid="paid-warning">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                This user already has an active paid subscription. Complimentary access should not replace or modify their billing subscription.
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Duration</label>
              <select value={premiumForm.duration} onChange={e => setPremiumForm({ ...premiumForm, duration: e.target.value })}
                data-testid="grant-duration" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value="7d">7 Days</option><option value="30d">30 Days</option><option value="90d">90 Days</option>
                <option value="6m">6 Months</option><option value="1y">1 Year</option>
                <option value="lifetime">Lifetime</option><option value="custom">Custom Expiration Date</option>
              </select>
            </div>
            {premiumForm.duration === "custom" && (
              <div>
                <label className="text-xs text-slate-500 block mb-1">Expiration Date</label>
                <input type="date" value={premiumForm.custom_expiration} onChange={e => setPremiumForm({ ...premiumForm, custom_expiration: e.target.value })}
                  data-testid="grant-custom-date" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 block mb-1">Reason</label>
              <input value={premiumForm.reason} onChange={e => setPremiumForm({ ...premiumForm, reason: e.target.value })}
                data-testid="grant-reason" placeholder="e.g. Partner / VIP / Compensation" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Internal Note (optional)</label>
              <textarea rows={2} value={premiumForm.internal_note} onChange={e => setPremiumForm({ ...premiumForm, internal_note: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-none" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={premiumForm.send_notification} onChange={e => setPremiumForm({ ...premiumForm, send_notification: e.target.checked })} className="rounded border-slate-600" data-testid="grant-notify" /> Send notification to user
            </label>
            <p className="text-xs text-slate-500">This user will receive complimentary Premium access. No payment will be recorded.</p>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setGrantModal(null)} className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm hover:bg-slate-700">Cancel</button>
              <button onClick={submitGrant} disabled={premiumBusy} data-testid="grant-submit" className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 disabled:opacity-50">Grant Premium</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage Premium Modal */}
      {manageModal && (
        <Modal title="Manage Complimentary Premium" onClose={() => setManageModal(null)}>
          <div className="space-y-4" data-testid="manage-premium-modal">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">User</span><span className="text-white font-medium">{manageModal.email}</span></div>
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">Current Expiration</span><span className="text-white">{manageModal.is_lifetime ? "Lifetime" : (manageModal.premium_expiration || "").slice(0, 10) || "—"}</span></div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">New Duration</label>
              <select value={premiumForm.duration} onChange={e => setPremiumForm({ ...premiumForm, duration: e.target.value })}
                data-testid="manage-duration" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value="7d">7 Days from now</option><option value="30d">30 Days from now</option><option value="90d">90 Days from now</option>
                <option value="6m">6 Months from now</option><option value="1y">1 Year from now</option>
                <option value="lifetime">Convert to Lifetime</option><option value="custom">Custom Expiration Date</option>
              </select>
            </div>
            {premiumForm.duration === "custom" && (
              <div><label className="text-xs text-slate-500 block mb-1">Expiration Date</label>
                <input type="date" value={premiumForm.custom_expiration} onChange={e => setPremiumForm({ ...premiumForm, custom_expiration: e.target.value })}
                  data-testid="manage-custom-date" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" /></div>
            )}
            <div><label className="text-xs text-slate-500 block mb-1">Reason for change</label>
              <input value={premiumForm.reason} onChange={e => setPremiumForm({ ...premiumForm, reason: e.target.value })} data-testid="manage-reason" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" /></div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setManageModal(null)} className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm hover:bg-slate-700">Cancel</button>
              <button onClick={submitManage} disabled={premiumBusy} data-testid="manage-submit" className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 text-black font-bold text-sm hover:bg-amber-400 disabled:opacity-50">Update Premium</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Revoke Premium Modal */}
      {revokeModal && (
        <Modal title="Revoke Complimentary Premium" onClose={() => setRevokeModal(null)}>
          <div className="space-y-4" data-testid="revoke-premium-modal">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">User</span><span className="text-white font-medium">{revokeModal.email}</span></div>
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">Membership Source</span><SourceBadge source={revokeModal.membership_source} /></div>
            <div className="flex items-center justify-between text-sm"><span className="text-slate-400">Current Expiration</span><span className="text-white">{revokeModal.is_lifetime ? "Lifetime" : (revokeModal.premium_expiration || "").slice(0, 10) || "—"}</span></div>
            {revokeModal.has_paid_subscription && (
              <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2.5 text-xs text-green-300">This user also has a paid subscription — their paid Premium access will remain active after revoking the complimentary grant.</div>
            )}
            <div><label className="text-xs text-slate-500 block mb-1">Revocation Reason</label>
              <input value={premiumForm.reason} onChange={e => setPremiumForm({ ...premiumForm, reason: e.target.value })} data-testid="revoke-reason" className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={premiumForm.send_notification} onChange={e => setPremiumForm({ ...premiumForm, send_notification: e.target.checked })} className="rounded border-slate-600" /> Send notification to user
            </label>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setRevokeModal(null)} className="flex-1 px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm hover:bg-slate-700">Cancel</button>
              <button onClick={submitRevoke} disabled={premiumBusy} data-testid="revoke-submit" className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white font-bold text-sm hover:bg-red-500 disabled:opacity-50">Revoke Premium</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ===== Sub-components =====
function MembershipBadge({ user }) {
  const t = user.membership_type;
  if (t === "premium") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/25 text-xs font-semibold" data-testid={`membership-${user.id}`}><Crown size={11} /> Premium</span>;
  if (t === "expired") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-400 border border-slate-600/40 text-xs font-medium" data-testid={`membership-${user.id}`}>Expired</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-800 text-slate-500 border border-slate-700 text-xs font-medium" data-testid={`membership-${user.id}`}>Free</span>;
}

function SourceBadge({ source }) {
  if (source === "paid") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-500/10 text-green-400 border border-green-500/20 text-[11px] font-medium">Paid</span>;
  if (source === "admin_grant") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[11px] font-medium">Admin Grant</span>;
  return <span className="text-xs text-slate-600">—</span>;
}

function UserActions({ user, onView, onEditRole, onMakeMod, onRevokeMod, onModPerms, onSuspend, onReactivate, onGrantPremium, onManagePremium, onRevokePremium }) {
  const hasComp = user.is_comp_member;
  const hasPaid = user.is_paid_member;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1.5 rounded hover:bg-slate-800 text-slate-400 outline-none focus:ring-2 focus:ring-blue-500/40" data-testid={`user-actions-${user.id}`} aria-label="User actions">
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" sideOffset={6} collisionPadding={12}
        className="w-52 z-[1000] bg-slate-800 border-slate-700 text-slate-200" data-testid={`user-menu-${user.id}`}>
        <DropdownMenuLabel className="text-slate-400 text-[11px]">Account</DropdownMenuLabel>
        <DropdownMenuItem onClick={onView} className="focus:bg-slate-700/60 text-xs cursor-pointer" data-testid={`menu-view-${user.id}`}><Eye size={14} className="mr-2" /> View Details</DropdownMenuItem>
        <DropdownMenuItem onClick={onEditRole} className="focus:bg-slate-700/60 text-xs cursor-pointer" data-testid={`menu-role-${user.id}`}><Shield size={14} className="mr-2" /> Edit Role</DropdownMenuItem>

        <DropdownMenuSeparator className="bg-slate-700" />
        <DropdownMenuLabel className="text-slate-400 text-[11px]">Premium</DropdownMenuLabel>
        {hasPaid && (
          <DropdownMenuItem onClick={onView} className="focus:bg-slate-700/60 text-xs cursor-pointer text-green-400" data-testid={`menu-view-sub-${user.id}`}><Star size={14} className="mr-2" /> View Subscription</DropdownMenuItem>
        )}
        {!hasComp && (
          <DropdownMenuItem onClick={onGrantPremium} className="focus:bg-slate-700/60 text-xs cursor-pointer text-amber-400" data-testid={`menu-grant-${user.id}`}><Gift size={14} className="mr-2" /> Grant Premium</DropdownMenuItem>
        )}
        {hasComp && (
          <>
            <DropdownMenuItem onClick={onManagePremium} className="focus:bg-slate-700/60 text-xs cursor-pointer text-amber-400" data-testid={`menu-manage-${user.id}`}><Zap size={14} className="mr-2" /> Manage Premium</DropdownMenuItem>
            <DropdownMenuItem onClick={onRevokePremium} className="focus:bg-slate-700/60 text-xs cursor-pointer text-red-400" data-testid={`menu-revoke-prem-${user.id}`}><Ban size={14} className="mr-2" /> Revoke Premium</DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator className="bg-slate-700" />
        <DropdownMenuLabel className="text-slate-400 text-[11px]">Moderation</DropdownMenuLabel>
        {!user.is_moderator && !user.is_suspended && (
          <DropdownMenuItem onClick={onMakeMod} className="focus:bg-slate-700/60 text-xs cursor-pointer text-green-400" data-testid={`menu-makemod-${user.id}`}><ShieldCheck size={14} className="mr-2" /> Make Moderator</DropdownMenuItem>
        )}
        {user.is_moderator && (
          <>
            <DropdownMenuItem onClick={onModPerms} className="focus:bg-slate-700/60 text-xs cursor-pointer text-purple-400"><FileText size={14} className="mr-2" /> Mod Permissions</DropdownMenuItem>
            <DropdownMenuItem onClick={onRevokeMod} className="focus:bg-slate-700/60 text-xs cursor-pointer text-red-400"><ShieldAlert size={14} className="mr-2" /> Revoke Moderator</DropdownMenuItem>
          </>
        )}
        {user.is_suspended ? (
          <DropdownMenuItem onClick={onReactivate} className="focus:bg-slate-700/60 text-xs cursor-pointer text-green-400" data-testid={`menu-reactivate-${user.id}`}><UserCheck size={14} className="mr-2" /> Reactivate User</DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={onSuspend} className="focus:bg-slate-700/60 text-xs cursor-pointer text-red-400" data-testid={`menu-suspend-${user.id}`}><Ban size={14} className="mr-2" /> Suspend User</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActionBtn({ icon: Icon, label, onClick, className = "" }) {
  return (
    <button onClick={onClick} className={cn("w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-700/50 transition-colors text-slate-300", className)}>
      <Icon size={14} /> {label}
    </button>
  );
}

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="modal-overlay">
      <div className={cn("bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full", wide ? "max-w-2xl" : "max-w-lg")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function PermissionCheckboxes({ allPermissions, selected, onChange }) {
  const categories = [...new Set(allPermissions.filter(p => p.category === 'moderation').map(p => p.category))];
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(p => p !== id) : [...selected, id]);
  };
  const modPerms = allPermissions.filter(p => p.category === 'moderation');
  return (
    <div className="space-y-2">
      <label className="text-xs text-slate-500 block">Custom Permissions</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
        {modPerms.map(p => (
          <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800/50 cursor-pointer text-xs">
            <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)}
              className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0" />
            <span className="text-slate-300">{p.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
